/**
 * Canonical business enums for KainOS.
 *
 * These values are persisted, sent over the wire and consumed by the AI layer,
 * so they are declared once here and reused everywhere. UI labels (Spanish) are
 * resolved at the presentation layer, never stored.
 */

export const UserRole = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  MEMBER: 'MEMBER',
  VIEWER: 'VIEWER',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** Ordered from most to least privileged; used for coarse permission checks. */
export const ROLE_RANK: Record<UserRole, number> = {
  OWNER: 5,
  ADMIN: 4,
  PROJECT_MANAGER: 3,
  MEMBER: 2,
  VIEWER: 1,
};

export const ProjectStatus = {
  PLANNING: 'PLANNING',
  IN_PROGRESS: 'IN_PROGRESS',
  AT_RISK: 'AT_RISK',
  ON_HOLD: 'ON_HOLD',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export const ProjectHealth = {
  NORMAL: 'NORMAL',
  ATTENTION: 'ATTENTION',
  CRITICAL: 'CRITICAL',
} as const;
export type ProjectHealth = (typeof ProjectHealth)[keyof typeof ProjectHealth];

export const ActivityStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  BLOCKED: 'BLOCKED',
  IN_REVIEW: 'IN_REVIEW',
  DONE: 'DONE',
  CANCELLED: 'CANCELLED',
} as const;
export type ActivityStatus = (typeof ActivityStatus)[keyof typeof ActivityStatus];

export const Priority = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const;
export type Priority = (typeof Priority)[keyof typeof Priority];

export const PRIORITY_RANK: Record<Priority, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

export const RiskSeverity = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const;
export type RiskSeverity = (typeof RiskSeverity)[keyof typeof RiskSeverity];

export const RiskProbability = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
} as const;
export type RiskProbability = (typeof RiskProbability)[keyof typeof RiskProbability];

export const RiskStatus = {
  PROPOSED: 'PROPOSED',
  OPEN: 'OPEN',
  MITIGATED: 'MITIGATED',
  ACCEPTED: 'ACCEPTED',
  CLOSED: 'CLOSED',
  DISMISSED: 'DISMISSED',
} as const;
export type RiskStatus = (typeof RiskStatus)[keyof typeof RiskStatus];

export const IssueStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
} as const;
export type IssueStatus = (typeof IssueStatus)[keyof typeof IssueStatus];

export const DecisionStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  DEFERRED: 'DEFERRED',
} as const;
export type DecisionStatus = (typeof DecisionStatus)[keyof typeof DecisionStatus];

export const MilestoneStatus = {
  PLANNED: 'PLANNED',
  AT_RISK: 'AT_RISK',
  DELAYED: 'DELAYED',
  ACHIEVED: 'ACHIEVED',
} as const;
export type MilestoneStatus = (typeof MilestoneStatus)[keyof typeof MilestoneStatus];

export const PhaseStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
} as const;
export type PhaseStatus = (typeof PhaseStatus)[keyof typeof PhaseStatus];

/** How an update entered the system. Drives the human-in-the-loop flow. */
export const UpdateSource = {
  MANUAL: 'MANUAL',
  TEXT: 'TEXT',
  AI: 'AI',
  SYSTEM: 'SYSTEM',
} as const;
export type UpdateSource = (typeof UpdateSource)[keyof typeof UpdateSource];

/** Human-in-the-loop lifecycle for anything an LLM proposes. */
export const ReviewState = {
  PROPOSED: 'PROPOSED',
  CONFIRMED: 'CONFIRMED',
  REJECTED: 'REJECTED',
} as const;
export type ReviewState = (typeof ReviewState)[keyof typeof ReviewState];

export const BlockerCategory = {
  MATERIAL: 'MATERIAL',
  EQUIPMENT: 'EQUIPMENT',
  PERSONNEL: 'PERSONNEL',
  PERMIT: 'PERMIT',
  DESIGN: 'DESIGN',
  WEATHER: 'WEATHER',
  SUPPLIER: 'SUPPLIER',
  DEPENDENCY: 'DEPENDENCY',
  OTHER: 'OTHER',
} as const;
export type BlockerCategory = (typeof BlockerCategory)[keyof typeof BlockerCategory];

/** Types of exception surfaced by the Attention center. */
export const AttentionType = {
  BLOCKER: 'BLOCKER',
  DELAY: 'DELAY',
  RISK: 'RISK',
  DECISION: 'DECISION',
  STALE: 'STALE',
} as const;
export type AttentionType = (typeof AttentionType)[keyof typeof AttentionType];

export const EvidenceKind = {
  PHOTO: 'PHOTO',
  DOCUMENT: 'DOCUMENT',
  VIDEO: 'VIDEO',
  OTHER: 'OTHER',
} as const;
export type EvidenceKind = (typeof EvidenceKind)[keyof typeof EvidenceKind];

export const EvidenceStatus = {
  PENDING_UPLOAD: 'PENDING_UPLOAD',
  UPLOADED: 'UPLOADED',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  FAILED: 'FAILED',
} as const;
export type EvidenceStatus = (typeof EvidenceStatus)[keyof typeof EvidenceStatus];

export const NotificationChannel = {
  IN_APP: 'IN_APP',
  EMAIL: 'EMAIL',
  PUSH: 'PUSH',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const ReportKind = {
  EXECUTIVE_SUMMARY: 'EXECUTIVE_SUMMARY',
  WEEKLY: 'WEEKLY',
  PROJECT_STATUS: 'PROJECT_STATUS',
} as const;
export type ReportKind = (typeof ReportKind)[keyof typeof ReportKind];
